import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, MenuBook as MenuBookIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { publishersService, Publisher } from '../services/firebaseFunctions';
import { formatFirestoreDateOnly } from '../utils/dateUtils';
import { operationErrorMessage } from '../utils/adminFeedback';
import { useAuth } from '../contexts/AuthContext';
import DestructiveActionDialog from '../components/DestructiveActionDialog';

const PublishersPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canManage = hasPermission('editoras', 'editar');
  const [publishers, setPublishers] = useState<Publisher[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPublisher, setEditingPublisher] = useState<Publisher | null>(null);
  const [formData, setFormData] = useState({ name: '' });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [submitting, setSubmitting] = useState(false);
  const [deletingPublisherId, setDeletingPublisherId] = useState<string | null>(null);
  const [publisherPendingDeletion, setPublisherPendingDeletion] = useState<Publisher | null>(null);

  const loadPublishers = async () => {
    try {
      setLoading(true);
      const data = await publishersService.getAll();
      setPublishers(data);
    } catch (error) {
      console.error('Error loading publishers:', error);
      setSnackbar({ open: true, message: 'Erro ao carregar editoras', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPublishers();
  }, []);

  const handleOpenDialog = (publisher?: Publisher) => {
    if (publisher) {
      setEditingPublisher(publisher);
      setFormData({ name: publisher.name });
    } else {
      setEditingPublisher(null);
      setFormData({ name: '' });
    }
    setOpenDialog(true);
  };

  const resetPublisherDialog = () => {
    setOpenDialog(false);
    setEditingPublisher(null);
  };

  const handleCloseDialog = () => {
    if (submitting) return;
    resetPublisherDialog();
  };

  const handleSubmit = async () => {
    const name = formData.name.trim();
    if (!name) {
      setSnackbar({ open: true, message: 'Nome é obrigatório', severity: 'error' });
      return;
    }
    try {
      setSubmitting(true);
      if (editingPublisher) {
        const result = await publishersService.update(editingPublisher.id, { name });
        if (result.success) {
          await loadPublishers();
          setSnackbar({ open: true, message: 'Editora atualizada com sucesso!', severity: 'success' });
          resetPublisherDialog();
        } else {
          setSnackbar({ open: true, message: operationErrorMessage('publisher', result), severity: 'error' });
        }
      } else {
        const result = await publishersService.create({ name });
        if (result.success) {
          await loadPublishers();
          setSnackbar({ open: true, message: 'Editora criada com sucesso!', severity: 'success' });
          resetPublisherDialog();
        } else {
          setSnackbar({ open: true, message: operationErrorMessage('publisher', result), severity: 'error' });
        }
      }
    } catch {
      setSnackbar({
        open: true,
        message: operationErrorMessage('publisher'),
        severity: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!publisherPendingDeletion) return;
    try {
      setDeletingPublisherId(publisherPendingDeletion.id);
      const result = await publishersService.delete(publisherPendingDeletion.id);
      if (result.success) {
        await loadPublishers();
        setSnackbar({ open: true, message: 'Editora excluída com sucesso!', severity: 'success' });
        setPublisherPendingDeletion(null);
      } else {
        setSnackbar({ open: true, message: operationErrorMessage('publisher', result), severity: 'error' });
      }
    } catch {
      setSnackbar({
        open: true,
        message: operationErrorMessage('publisher'),
        severity: 'error',
      });
    } finally {
      setDeletingPublisherId(null);
    }
  };

  const formatDate = (date: any) => formatFirestoreDateOnly(date);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Editoras
        </Typography>
        <Tooltip title={canManage ? '' : 'Você não tem permissão de edição no módulo Editoras.'}>
          <span>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()} disabled={!canManage}>
              Nova editora
            </Button>
          </span>
        </Tooltip>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Nome</TableCell>
              <TableCell>Criado em</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {publishers.map((publisher) => {
              const rowBusy = deletingPublisherId === publisher.id;
              return (
                <TableRow
                  key={publisher.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/publishers/${publisher.id}`)}
                >
                  <TableCell>{publisher.name}</TableCell>
                  <TableCell>{formatDate(publisher.createdAt)}</TableCell>
                  <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <IconButton size="small" onClick={() => navigate(`/publishers/${publisher.id}`)} color="primary" title="Ver livros">
                      <MenuBookIcon />
                    </IconButton>
                    <Tooltip title={canManage ? 'Editar' : 'Você não tem permissão de edição no módulo Editoras.'}>
                      <span>
                        <IconButton size="small" onClick={() => handleOpenDialog(publisher)} color="primary" disabled={!canManage || rowBusy}>
                          <EditIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={canManage ? 'Excluir' : 'Você não tem permissão de edição no módulo Editoras.'}>
                      <span>
                        <IconButton size="small" onClick={() => setPublisherPendingDeletion(publisher)} color="error" disabled={!canManage || rowBusy}>
                          <DeleteIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPublisher ? 'Editar editora' : 'Nova editora'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nome da editora"
            value={formData.name}
            onChange={(e) => setFormData({ name: e.target.value })}
            margin="normal"
            autoFocus
            disabled={submitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {submitting ? (editingPublisher ? 'Salvando…' : 'Criando…') : editingPublisher ? 'Atualizar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      <DestructiveActionDialog
        open={Boolean(publisherPendingDeletion)}
        entityLabel="Editora"
        entityName={publisherPendingDeletion?.name || ''}
        busy={Boolean(publisherPendingDeletion && deletingPublisherId === publisherPendingDeletion.id)}
        onCancel={() => setPublisherPendingDeletion(null)}
        onConfirm={() => void handleDelete()}
      />

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PublishersPage;
